/* VELOCHI — ONE OBJECT / FIVE CHAPTERS
 * Timeline is deterministic from scroll position and reversible, not a canned video.
 * Real VELOCHI product photography + art-directed photographic cutaway layers.
 */
(()=>{
  'use strict';
  const root=document.querySelector('[data-vx]');
  if(!root)return;
  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches || !('requestAnimationFrame' in window) || !('position' in document.documentElement.style))return;
  const els={};
  const find=s=>root.querySelector(s);
  const all=s=>[...root.querySelectorAll(s)];
  for(const id of ['intact','split-l','split-r','shell','mantle','outer','inner']) els[id]=find(`[data-vx-piece="${id}"]`);
  const chapters=all('[data-vx-chapter]');
  const orbits={one:find('[data-vx-orbit="one"]'),two:find('[data-vx-orbit="two"]'),three:find('[data-vx-orbit="three"]')};
  const leaders=all('[data-vx-leader]');
  const progress=find('[data-vx-progress]'),indexEl=find('[data-vx-index]');
  const statusEl=find('[data-vx-status]'),specCounter=find('[data-vx-spec-counter]'),beam=find('[data-vx-beam]');
  const impact=find('[data-vx-impact]'),shockwave=find('[data-vx-shockwave]');
  const railBtns=all('.vx__rail-dots [data-vx-scroll-target]');
  if(Object.values(els).some(x=>!x)||chapters.length!==5)return;
  const clamp=(x,a=0,b=1)=>Math.min(b,Math.max(a,x));
  const smooth=x=>{x=clamp(x);return x*x*(3-2*x)};
  const map=(p,a,b)=>smooth((p-a)/(b-a));
  const mix=(a,b,t)=>a+(b-a)*t;
  const key=(p,points)=>{
    if(p<=points[0][0])return points[0][1];
    for(let i=1;i<points.length;i++)if(p<=points[i][0])return mix(points[i-1][1],points[i][1],map(p,points[i-1][0],points[i][0]));
    return points[points.length-1][1];
  };
  // Deliberate dwell time between the four physical separations.
  const centers=[.078,.298,.515,.742,.958];
  const transitions=[.189,.407,.625,.843];
  const descriptions=['OBJECT INTACT','01 / URETHANE COVER','02 / STABILITY MANTLE','03 / OUTER ENERGY CORE','04 / INNER ENERGY CORE'];
  let activeChapter=-1,scrollTarget=0,progressNow=0,frame=0,isNear=true;
  let width=0,height=0,ball=400,mobile=false,mouseX=0,mouseY=0,smoothMouseX=0,smoothMouseY=0,lastTime=0;

  function currentFromPosition(){
    const rect=root.getBoundingClientRect();
    const distance=Math.max(1,root.offsetHeight-innerHeight);
    scrollTarget=clamp(-rect.top/distance);
  }
  function remeasure(){
    width=innerWidth;height=innerHeight;mobile=width<=900;
    if(mobile)ball=clamp(Math.min(width*.76,height*.435),195,360);
    else ball=clamp(Math.min(width*.345,height*.63),310,565);
    root.style.setProperty('--vx-ball-size',ball.toFixed(1)+'px');
    root.style.setProperty('--vx-reactor-center',(mobile?51.5:68.7)+'%');
    root.style.setProperty('--vx-reactor-y',(mobile?(height<680?44.3:46.5):51)+'%');
    currentFromPosition();schedule();
  }
  function compositePosition(name,x,y,opacity,scale=1,rotation=0){
    const node=els[name];
    const tx=(x-.5)*width,ty=(y-.5)*height;
    node.style.opacity=clamp(opacity).toFixed(4);
    node.style.visibility=opacity>.001?'visible':'hidden';
    node.style.transform=`translate3d(-50%,-50%,0) translate3d(${tx.toFixed(2)}px,${ty.toFixed(2)}px,0) rotate(${rotation.toFixed(3)}deg) scale(${scale.toFixed(4)})`;
  }
  function chapterUI(p){
    const active=transitions.findIndex(stop=>p<stop);
    const index=active<0?4:active;
    // Clear two-beat replacement: the old panel fades OUT completely before
    // the incoming panel fades IN. Never stack two legible chapter panels.
    for(let i=0;i<chapters.length;i++){
      const enter=i===0?1:map(p,transitions[i-1],transitions[i-1]+.019);
      const leave=i===4?1:1-map(p,transitions[i]-.019,transitions[i]);
      const opacity=clamp(enter*leave);
      const section=chapters[i];
      section.style.opacity=opacity.toFixed(4);
      section.style.visibility=opacity>.001?'visible':'hidden';
      section.style.transform=`translate3d(0,${((1-opacity)*13).toFixed(2)}px,0)`;
      const interactive=i===index&&opacity>.80;
      section.inert=!interactive;
      section.style.pointerEvents=interactive?'auto':'none';
      section.setAttribute('aria-hidden',interactive?'false':'true');
      section.classList.toggle('is-active',interactive);
    }
    if(activeChapter!==index){
      activeChapter=index;
      root.classList.toggle('is-finale',index===4);
      if(indexEl)indexEl.textContent=String(index+1).padStart(2,'0');
      if(statusEl)statusEl.textContent=descriptions[index];
      if(specCounter)specCounter.textContent=index===0?'OBJECT / 001':`${String(index).padStart(2,'0')} / FOUR LAYERS`;
      railBtns.forEach((b,i)=>{
        b.classList.toggle('is-current',i===index);
        if(i===index)b.setAttribute('aria-current','step');
        else b.removeAttribute('aria-current');
      });
    }
  }
  function render(p,t){
    chapterUI(p);
    if(progress)progress.style.height=(p*100).toFixed(2)+'%';
    const small=mobile;
    // V7.2 — The exploded object is fitted by the ACTUAL widths of the four
    // photographic half-spheres, not an approximate center-to-center distance.
    // This ensures the red center is genuinely detached at the final frame,
    // even on a 320px phone, while preserving the V4 photographic materials.
    const y=key(p,[[0,small?(height<680?.443:.465):.51],[.75,small?(height<680?.443:.465):.51],[.92,small?.455:.51],[1,small?.455:.51]]);
    const parallax=small?0:1;
    const fraction=X=>X+smoothMouseX*.005*parallax;
    const heightF=Y=>Y+smoothMouseY*.006*parallax;
    const intactX=small?.515:.687;
    const splitIn=map(p,.175,.197),splitMove=map(p,.21,.289);
    const intactFade=1-map(p,.188,.216);
    const splitFade=splitIn*(1-map(p,.232,.282));

    // Drawn half-sphere widths (V4 photographic assets, as authored in V7 CSS).
    // Multiply these by the common camera scale to obtain the real on-screen
    // footprint. No separate choreography exists for desktop and mobile.
    const widthRatios=[.851,.548,.563,.49];
    const totalRatio=widthRatios.reduce((a,b)=>a+b,0);
    const smallMargin=clamp(width*.041,12,21);
    const gap=small?clamp(width*.026,9,14):clamp(width*.014,15,24);
    // Protect the final chapter's left-side heading on desktop. On a phone,
    // use the whole screen with consistent margins instead.
    const zoneStart=small?smallMargin:width*.385;
    const zoneEnd=small?width-smallMargin:width*.963;
    const available=Math.max(100,zoneEnd-zoneStart);
    const maxFit=small?.74:.81;
    const finalFit=Math.min(maxFit,Math.max(.25,(available-3*gap)/(ball*totalRatio)));

    // Continuous pullback: the object actually shrinks enough to reveal the
    // full assembly instead of sending the red core beyond the right edge.
    const shrink=key(p,[[0,1],[.17,1.015],[.23,.94],
      [.385,mix(1,finalFit,.47)],
      [.584,mix(1,finalFit,.66)],
      [.786,mix(1,finalFit,.82)],
      [.958,finalFit],[1,finalFit]]);
    compositePosition('intact',fraction(intactX),heightF(y),intactFade,shrink*(1+.012*map(p,.06,.155)),0);
    const fracture=(small?.105:.060)*splitMove;
    compositePosition('split-l',fraction(intactX-fracture),heightF(y),splitFade,shrink,-2.2*splitMove);
    compositePosition('split-r',fraction(intactX+fracture),heightF(y),splitFade,shrink,2.2*splitMove);

    // Final centers: face-to-face spacing, including an explicit gap between
    // amber and crimson. The whole assembly is then centered within the stage.
    const sizes=widthRatios.map(r=>r*ball*finalFit);
    const occupied=sizes.reduce((a,b)=>a+b,0)+3*gap;
    let edge=zoneStart+(available-occupied)*.5;
    const ends=sizes.map((size,i)=>{
      const center=edge+size*.5;
      edge+=size+(i<3?gap:0);
      return center;
    });
    const [shellEndPx,mantleEndPx,outerEndPx,innerEndPx]=ends;
    root.dataset.v72Gaps=[0,1,2].map(i=>(ends[i+1]-sizes[i+1]*.5)-(ends[i]+sizes[i]*.5)).map(n=>n.toFixed(1)).join(',');
    const originPx=intactX*width;

    // Strictly sequential movement windows: shell, mantle, amber, then red.
    const shellOpen=map(p,.248,.385);
    const mantleOpen=map(p,.458,.584);
    const outerOpen=map(p,.661,.786);
    const innerOpen=map(p,.858,.958);
    const shellStartPx=originPx;
    // The mantle is nested behind the white cover's open edge.
    const mantleStartPx=shellEndPx+sizes[0]*.12;
    // The amber core is born behind the graphite mantle's rim.
    const outerStartPx=mantleEndPx+sizes[1]*.13;
    // Red starts INSIDE the amber cavity after the amber core finishes moving,
    // then travels beyond its exposed rim. It is occluded by the amber half
    // shell until it emerges — no teleport or sudden duplicate sphere.
    const innerStartPx=outerEndPx+sizes[2]*.12;
    const xShell=mix(shellStartPx,shellEndPx,shellOpen);
    const xMantle=mix(mantleStartPx,mantleEndPx,mantleOpen);
    const xOuter=mix(outerStartPx,outerEndPx,outerOpen);
    const xInner=mix(innerStartPx,innerEndPx,innerOpen);

    const shellVisible=map(p,.255,.286);
    const mantleVisible=map(p,.383,.435);
    const outerVisible=map(p,.591,.645);
    const innerVisible=map(p,.843,.88);
    compositePosition('inner',fraction(xInner/width),heightF(y),innerVisible,shrink);
    compositePosition('outer',fraction(xOuter/width),heightF(y),outerVisible,shrink);
    compositePosition('mantle',fraction(xMantle/width),heightF(y),mantleVisible,shrink);
    compositePosition('shell',fraction(xShell/width),heightF(y),shellVisible,shrink);

    // Cinematic payoff follows the red component, not the older center-point
    // shockwave: bright edge light blooms as it clears the amber aperture.
    const redPulse=map(p,.864,.912)*(1-map(p,.961,.995));
    const core=els.inner;
    core.style.setProperty('--vx-red-pulse',redPulse.toFixed(4));
    root.style.setProperty('--vx-v72-core-x',(xInner/width*100).toFixed(3)+'%');
    root.style.setProperty('--vx-v72-core-y',(y*100).toFixed(3)+'%');
    if(beam){
      const intensity=map(p,.208,.235)*(1-map(p,.305,.352));
      beam.style.opacity=(intensity*.82).toFixed(3);
      beam.style.transform=`translate3d(${(width*(intactX-.66)).toFixed(2)}px,0,0)`;
    }
    // Independent calibration rings react to scroll, without arbitrary high-frequency motion.
    const energy=map(p,.14,.89);
    const ring=(node,deg,scale)=>{
      if(!node)return;
      node.style.transform=`translate(-50%,-50%) rotate(${deg.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
      node.style.opacity=(.43+energy*.13).toFixed(3);
    };
    ring(orbits.one,p*565,1+energy*.13);
    ring(orbits.two,-p*430,1+energy*.14);
    ring(orbits.three,p*730,1+energy*.17);
    // A restrained shockwave is triggered by each separation, tied to scroll progress.
    const peaks=[.285,.474,.681,.882];
    const impulse=peaks.reduce((value,center)=>Math.max(value,Math.exp(-Math.pow((p-center)/.026,2))),0);
    if(impact){impact.style.opacity=(impulse*.70).toFixed(3);impact.style.transform=`translate(-50%,-50%) scale(${(1+impulse*.38).toFixed(3)})`;}
    if(shockwave){shockwave.style.opacity=(impulse*.5).toFixed(3);shockwave.style.transform=`translate(-50%,-50%) scale(${(1.05+(1-impulse)*1.5).toFixed(3)})`;}
    const finale=map(p,.875,.935);
    leaders.forEach(el=>{el.style.opacity=(finale*.7).toFixed(3);el.style.transform=`translate3d(0,${((1-finale)*10).toFixed(1)}px,0)`});
    const backdrop=root.querySelector('.vx__aurora--b');
    if(backdrop)backdrop.style.transform=`translate3d(${(p*width*.035).toFixed(1)}px,0,0)`;
  }
  function frameTick(t){
    frame=0;
    if(!isNear||document.hidden)return;
    const dt=lastTime?Math.min(40,t-lastTime):16;lastTime=t;
    const response=1-Math.exp(-dt*.018);
    progressNow+=(scrollTarget-progressNow)*response;
    smoothMouseX+=(mouseX-smoothMouseX)*.085;
    smoothMouseY+=(mouseY-smoothMouseY)*.085;
    if(Math.abs(scrollTarget-progressNow)<.00007)progressNow=scrollTarget;
    render(progressNow,t);
    // Idle animation stops once deterministic positions have settled.
    if(isNear && (Math.abs(scrollTarget-progressNow)>.00009 || Math.abs(mouseX-smoothMouseX)>.0007 || Math.abs(mouseY-smoothMouseY)>.0007)) frame=requestAnimationFrame(frameTick);
  }
  function schedule(){if(isNear&&!frame)frame=requestAnimationFrame(frameTick)}
  function scrollToIndex(i){
    const top=window.scrollY+root.getBoundingClientRect().top;
    const distance=Math.max(1,root.offsetHeight-window.innerHeight);
    window.scrollTo({top:top+distance*centers[clamp(i,0,4)],behavior:'smooth'});
  }
  // Native product choices reflect actual Shopify product/prices rendered by Liquid.
  const choices=all('[data-vx-product]'),selectedImage=find('[data-vx-selected-image]'),selectedName=find('[data-vx-selected-name]'),selectedPrice=find('[data-vx-selected-price]'),selectedLink=find('[data-vx-selected-link]');
  choices.forEach(button=>button.addEventListener('click',()=>{
    choices.forEach(b=>{const selected=b===button;b.classList.toggle('is-selected',selected);b.setAttribute('aria-pressed',String(selected))});
    if(selectedImage){selectedImage.src=button.dataset.image;selectedImage.alt=button.dataset.name||'Selected Velochi product'}
    if(selectedName)selectedName.textContent=button.dataset.name;
    if(selectedPrice)selectedPrice.textContent=button.dataset.price;
    if(selectedLink){selectedLink.href=button.dataset.href;selectedLink.setAttribute('aria-label','Shop '+button.dataset.name)}
  }));
  const faq=all('.vx__faq-list details');
  faq.forEach(d=>d.addEventListener('toggle',()=>{if(d.open)faq.forEach(other=>{if(other!==d)other.open=false})}));
  const tabDescriptions=[
    'VELOCHI exists for golfers who care about precision beyond the scorecard. To us, the smallest details are never incidental.',
    'We begin with the object itself: structure, material, geometry, and the relationship between each component. ICON TOUR 01 is our expression of that approach.',
    'To make considered golf equipment for players who believe confidence is built through preparation, practice and attention to detail.'
  ];
  const tabs=all('[data-vx-about-tab]'),aboutPanel=find('[data-vx-about-panel]');
  function chooseTab(tab,focus=false){
    tabs.forEach(b=>{const chosen=b===tab;b.classList.toggle('is-selected',chosen);b.setAttribute('aria-selected',String(chosen));b.tabIndex=chosen?0:-1});
    if(aboutPanel)aboutPanel.textContent=tabDescriptions[Number(tab.dataset.vxAboutTab)||0];
    if(focus)tab.focus();
  }
  tabs.forEach(tab=>{
    tab.addEventListener('click',()=>chooseTab(tab));
    tab.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();const idx=tabs.indexOf(tab);chooseTab(tabs[(idx+(e.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length],true)}});
  });
  all('[data-vx-scroll-target]').forEach(button=>button.addEventListener('click',e=>{e.preventDefault();scrollToIndex(Number(button.dataset.vxScrollTarget)||0)}));
  // Enhancements turn on only after markup, controls and geometry are available.
  root.classList.add('is-motion');
  root.querySelectorAll('[data-vx-chapter]').forEach((node,i)=>{node.inert=i!==0;node.setAttribute('aria-hidden',i===0?'false':'true')});
  const observer=new IntersectionObserver(entries=>{
    isNear=entries.some(e=>e.isIntersecting);
    if(isNear){currentFromPosition();schedule()}else if(frame){cancelAnimationFrame(frame);frame=0}
  },{rootMargin:'130px 0px'});
  observer.observe(root);
  window.addEventListener('scroll',()=>{currentFromPosition();schedule()},{passive:true});
  window.addEventListener('resize',remeasure,{passive:true});
  window.addEventListener('pageshow',remeasure);
  window.addEventListener('pagehide',()=>{if(frame)cancelAnimationFrame(frame);frame=0});
  root.addEventListener('pointermove',e=>{
    if(!window.matchMedia('(pointer:fine)').matches)return;
    mouseX=clamp(e.clientX/innerWidth-.5,-.5,.5);
    mouseY=clamp(e.clientY/innerHeight-.5,-.5,.5);
  },{passive:true});
  root.addEventListener('pointerleave',()=>{mouseX=0;mouseY=0});
  remeasure();
  // Deep links from product, collection and footer navigation open a particular chapter.
  const chapterName=new URLSearchParams(location.search).get('chapter');
  const chapterIndex={shop:1,faq:2,about:3,engineering:4};
  if(chapterName&&Object.prototype.hasOwnProperty.call(chapterIndex,chapterName)){
    requestAnimationFrame(()=>scrollToIndex(chapterIndex[chapterName]));
  }
})();
